import {
  SimpleForm,
  TextInput,
  SaveButton,
  Toolbar,
  TextArrayInput
} from 'react-admin'
import { Box, Typography, Grid, Alert, Chip } from '@mui/material'
import { PartnerGeneralInfo } from '../types/PartnerGeneralInfo'
import { FieldValues } from 'react-hook-form'
import useAccount from '../hooks/useAccount'
import useAlert from '@/components/Alert/hooks/useAlert'
import { arrayToCommaSeparatedString } from '@/utils/arrayToCommaSeparatedString'
import InfoTooltip from '@/components/InfoTooltip'

const COPY_INFO_TEXT = 'Email Copied to clipboard!'
export type VendorAccountInfoProps = {
  partner: PartnerGeneralInfo
}

const styles = {
  helpIcon: {
    fontSize: '16px',
    color: 'grey.500',
    marginLeft: 0.5,
    verticalAlign: 'middle'
  },
  container: {
    padding: 2
  },
  typography: {
    marginTop: 2
  },
  formBox: {
    display: 'flex',
    alignItems: 'center'
  }
}

const formatRecord = (partner: PartnerGeneralInfo) => {
  const parseCommaSeparated = (value?: string): string[] =>
    value && value.trim() !== '' ? value.split(',').map((e) => e.trim()) : []

  return {
    ...partner,
    supportContactStackCommerce: parseCommaSeparated(
      partner.supportContactStackCommerce
    ),
    supportContactCustomers: parseCommaSeparated(
      partner.supportContactCustomers
    ),
    fulfillmentContact: parseCommaSeparated(partner.fulfillmentContact)
  }
}

export const VendorAccountInfo = ({ partner }: VendorAccountInfoProps) => {
  const { updateVendorInfo } = useAccount()
  const { alertInfo } = useAlert()

  const submitInfoUpdate = (values: FieldValues) => {
    const mergedData = {
      primaryContact: values.primaryContact,
      phoneNumber: values.phoneNumber,
      fulfillmentContact: arrayToCommaSeparatedString(
        values.fulfillmentContact
      ),
      supportContactCustomers: arrayToCommaSeparatedString(
        values.supportContactCustomers
      ),
      supportContactStackCommerce: arrayToCommaSeparatedString(
        values.supportContactStackCommerce
      ),
      supportFormUrl: values.supportFormUrl
    }

    updateVendorInfo(mergedData)
  }

  const SaveOnlyToolbar = () => (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  )

  return (
    <Box sx={styles.container}>
      {partner.addressVerificationState === 'unverified' && (
        <Alert severity="info">
          We have implemented an address verification update, and need you to
          take a moment to verify your fulfillment address below. Thank you!
        </Alert>
      )}
      <SimpleForm
        data-testid="update-vendor-info-form"
        toolbar={<SaveOnlyToolbar />}
        record={formatRecord(partner)}
        onSubmit={submitInfoUpdate}
      >
        <Typography variant="h6" gutterBottom sx={styles.typography}>
          General Information
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              source="primaryContact"
              label="Primary Contact"
              type="email"
              fullWidth
              isRequired
              inputProps={{ 'data-testid': 'primary-contact' }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              source="phoneNumber"
              label="Phone Number"
              fullWidth
              inputProps={{ 'data-testid': 'phone-number' }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={styles.formBox}>
              <TextArrayInput
                source="supportContactStackCommerce"
                label="Support Contact (StackCommerce)"
                helperText="Add emails"
                fullWidth
                // We're aware that `renderTags` prop is deprecated. However, react-admin (v5.8.1) still uses it internally
                renderTags={(value: readonly string[], getTagProps) =>
                  value.map((option: string, index: number) => {
                    const { key, ...tagProps } = getTagProps({ index })
                    const trimmedOption = option.trim()
                    return (
                      <Chip
                        label={trimmedOption}
                        key={key}
                        {...tagProps}
                        onClick={() => {
                          navigator.clipboard.writeText(trimmedOption)
                          alertInfo(COPY_INFO_TEXT)
                        }}
                      />
                    )
                  })
                }
              />
              <InfoTooltip
                label=""
                tooltipText="Please provide a contact email for our Customer Support Team in order
                to assist with the resolution of customer inquiries. Please note: this contact
                WILL NOT be provided directly to customers."
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={styles.formBox}>
              <TextArrayInput
                source="supportContactCustomers"
                label="Support Contact (Customers)"
                helperText="Add emails"
                fullWidth
                renderTags={(value: readonly string[], getTagProps) =>
                  value.map((option: string, index: number) => {
                    const { key, ...tagProps } = getTagProps({ index })
                    const trimmedOption = option.trim()
                    return (
                      <Chip
                        label={trimmedOption}
                        key={key}
                        {...tagProps}
                        onClick={() => {
                          navigator.clipboard.writeText(trimmedOption)
                          alertInfo(COPY_INFO_TEXT)
                        }}
                      />
                    )
                  })
                }
              />
              <InfoTooltip
                label=""
                tooltipText="Please provide a direct contact email for our customers.
                Our Customer Support Team will relay this to customers in the case that
                they are unable resolve a complex issue without your expertise."
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextArrayInput
              source="fulfillmentContact"
              label="Fulfillment Contact"
              helperText="Add emails"
              fullWidth
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => {
                  const { key, ...tagProps } = getTagProps({ index })
                  const trimmedOption = option.trim()
                  return (
                    <Chip
                      label={trimmedOption}
                      key={key}
                      {...tagProps}
                      onClick={() => {
                        navigator.clipboard.writeText(trimmedOption)
                        alertInfo(COPY_INFO_TEXT)
                      }}
                    />
                  )
                })
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={styles.formBox}>
              <TextInput
                source="supportFormUrl"
                label="Support Form URL"
                fullWidth
                inputProps={{ 'data-testid': 'support-url' }}
              />
              <InfoTooltip
                label=""
                tooltipText="Do you have a customer Q&A online form? Please provide the URL here."
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              source="fulfillmentPhoneNumber"
              label="Fulfillment Phone Number"
              fullWidth
            />
          </Grid>
        </Grid>
      </SimpleForm>
    </Box>
  )
}
